import { Grid, makeStyles } from "@material-ui/core";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import FilesBreadcrumbs from "features/common/FilesBreadcrumbs";
import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  ADMIN,
  ADMIN_PROFILE_TYPES,
  CASE_ADMIN_PROFILE_TYPES,
  CLOSED,
} from "app/constants";
import { getDispute } from "slices/disputeSlice";
import Files from "./Files";
import {
  getFiles,
  getFolder,
  getFolders,
  getProfilesOfDispute,
} from "./FileApi";
import { removeSessionStorage } from "features/tiac_header_waiting/TiacAppHeader";
import { isACCB, isDifc, isMSME, isSebi } from "Routes/Routes";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: "2rem",
    [theme.breakpoints.down("sm")]: {
      marginTop: "1.5rem",
    },
    [theme.breakpoints.down("xs")]: {
      marginTop: "1rem",
    },
  },
}));

export default function FileContainer() {
  let { disputeId, folder_id } = useParams();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const profile = useSelector((state) => state.profile, shallowEqual);

  const isHidePermissionsFeature = isACCB;

  const [dispute, setDispute] = useState();
  const [loading, setLoading] = useState(true);
  // closed cases: files stay viewable, but nothing new can be uploaded or created
  const isReadOnly = dispute?.status === CLOSED;

  const [parent_folder, setParent_folder] = useState();
  const [allFiles, setAllFiles] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]);

  useEffect(() => {
    dispatch(getDispute(disputeId)).then((res) => {
      setDispute(res.results[0]);
      setLoading(false);
    });
    getProfiles();
  }, []);

  const getProfiles = () => {
    getProfilesOfDispute(disputeId)
      .then((res) => {
        let profiles = [];
        res.data.details.claimant.forEach((item) => {
          let p = item;
          p.profile.profile_type = "Claimant";
          if (p.profile.id !== profile.profile.id) profiles.push(p);
        });
        res.data.details.claimant_lawyer.forEach((item) => {
          let p = item;
          p.profile.profile_type = "Claimant's Counsel";
          if (p.profile.id !== profile.profile.id) profiles.push(p);
        });
        res.data.details.respondent.forEach((item) => {
          let p = item;
          p.profile.profile_type = isDifc ? "Defendant" : "Respondent";
          if (p.profile.id !== profile.profile.id) profiles.push(p);
        });
        res.data.details.respondent_lawyer.forEach((item) => {
          let p = item;
          p.profile.profile_type = isDifc
            ? "Defendant's Counsel"
            : "Respondent's Counsel";
          if (p.profile.id !== profile.profile.id) profiles.push(p);
        });
        res.data.details.professionals.forEach((item) => {
          let p = item;
          if (p.profile.profile_type === "arbitrator_rv") {
            p.profile.profile_type = "Arbitrator";
          } else if (p.profile.profile_type === "witness") {
            p.profile.profile_type = "Witness";
          } else if (p.profile.profile_type === "witness_rv") {
            p.profile.profile_type = "Barrister";
          } else if (p.profile.profile_type === "expert_rv") {
            p.profile.profile_type = "Solicitor";
          } else {
            p.profile.profile_type =
              isSebi || isMSME ? "Conciliator" : "Mediator";
          }
          if (p.profile.id !== profile.profile.id) profiles.push(p);
        });
        setAllProfiles(profiles);
      })
      .catch((error) => {});
  };

  const reload = () => {
    setAllFiles([]);
    setAllFolders([]);
    getFiles(disputeId, folder_id).then((res) => {
      setAllFiles(res.data);
    });
    getFolders(disputeId, folder_id).then((res) => {
      setAllFolders(res.data);
    });
    getFolder(folder_id).then((response) => {
      setParent_folder(response.data[0]);
      if (response.data.length > 0) {
        let routes = getParent(response.data[0]);
        routes = routes.reverse();
        setAllRoutes(routes);
      } else {
        setAllRoutes([]);
      }
    });
  };

  const getParent = (parent) => {
    let arr = [];
    arr.push({ name: parent.name, id: parent.id });
    if (parent.parent_folder) {
      let t = getParent(parent.parent_folder);
      t.map((item) => {
        arr.push(item);
      });
    }
    return arr;
  };

  useEffect(() => {
    reload();
  }, [folder_id]);

  if (loading) return <CircularCenteredLoader />;

  const profileType = profile.profile.profile_type;
  const isAdminProfile = ADMIN_PROFILE_TYPES.includes(profileType);
  // Dashboard / case / Files / each folder down to the current one.
  const crumbs = [
    {
      key: "dashboard",
      label: "Dashboard",
      onClick: () => {
        if (isAdminProfile) removeSessionStorage();
        history.push("/");
      },
    },
    {
      key: "case",
      label: dispute.name,
      onClick: () => {
        if (CASE_ADMIN_PROFILE_TYPES.includes(profileType)) {
          sessionStorage.setItem("tiac_overview_caseid", dispute.id);
          history.push(`/`);
        } else if (profileType === ADMIN) {
          sessionStorage.setItem("caseid", dispute.id);
          history.push(`/`);
        } else history.push(`/disputes/${dispute.id}/`);
      },
    },
    {
      key: "files",
      label: "Files",
      onClick: () => history.push(`/disputes/${dispute.id}/files/0`),
    },
    ...allRoutes.map((route) => ({
      key: route.id,
      label: route.name,
      onClick: () => history.push(`/disputes/${dispute.id}/files/${route.id}`),
    })),
  ];

  return (
    <Grid container justify="center">
      <Grid item lg={10} md={10} sm={11} xs={11} className={classes.root}>
        <FilesBreadcrumbs
          crumbs={crumbs}
          folderKey={folder_id}
          onBack={() => {
            if (isAdminProfile) removeSessionStorage();
            history.goBack();
          }}
        />
        <Files
          disputeId={disputeId}
          folder_id={folder_id}
          allFiles={allFiles}
          allFolders={allFolders}
          reload={reload}
          allProfiles={allProfiles}
          profile={profile.profile}
          parent_folder={parent_folder}
          isHidePermissionsFeature={isHidePermissionsFeature}
          isReadOnly={isReadOnly}
        />
      </Grid>
    </Grid>
  );
}
